import React, { useState } from 'react';
import {
  TrashIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  EyeSlashIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  MapPinIcon,
  MicrophoneIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { deleteAdminChat } from '../api/adminChats';

const STATUS_FILTERS = [
  { value: '', label: 'Все' },
  { value: 'active', label: 'Активные' },
  { value: 'deleted', label: 'Скрытые' },
];

const SORT_OPTIONS = [
  { value: 'updated_at:desc', label: 'Сначала свежие' },
  { value: 'updated_at:asc', label: 'Сначала давние' },
  { value: 'created_at:desc', label: 'Новые чаты' },
  { value: 'message_count:desc', label: 'Больше сообщений' },
];

const AVATAR_COLORS = ['bg-indigo-500', 'bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500'];

export const Avatar = ({ user, className = 'h-8 w-8 text-xs' }) => (
  <div
    className={`${className} ${AVATAR_COLORS[(user?.id || 0) % AVATAR_COLORS.length]} flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-2 ring-bg-card`}
    title={user?.name}
  >
    {(user?.name || '?').trim()[0]?.toUpperCase() || '?'}
  </div>
);

export const relativeTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч`;
  if (diff < 172800) return 'вчера';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const previewIcon = (type) => {
  switch (type) {
    case 'file': return PaperClipIcon;
    case 'image': return PhotoIcon;
    case 'audio': return MicrophoneIcon;
    case 'location': return MapPinIcon;
    default: return null;
  }
};

const KPI = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-block border border-border-primary bg-bg-card p-4 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-text-secondary">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-text-primary">{value}</p>
      </div>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </div>
);

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('ru-RU'));

const ChatList = ({
  chats,
  statistics,
  pagination,
  filters,
  loading,
  error,
  onChatSelect,
  onFilterChange,
  onPageChange,
  onRefresh,
}) => {
  const [confirmChat, setConfirmChat] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAdminChat(confirmChat.id);
      setConfirmChat(null);
      onRefresh();
    } catch (err) {
      console.error('Error deleting chat:', err);
      setDeleteError(/403/.test(err.message) ? 'Системные чаты нельзя скрыть.' : err.message);
    } finally {
      setDeleting(false);
    }
  };

  const sortValue = `${filters.sort_by}:${filters.sort_order}`;
  const from = (pagination.page - 1) * pagination.limit + 1;
  const to = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI icon={ChatBubbleLeftRightIcon} label="Всего чатов" value={fmt(statistics?.total_chats)} tone="bg-indigo-500/15 text-indigo-500" />
        <KPI icon={ClockIcon} label="Сообщений за 24 ч" value={fmt(statistics?.recent_messages_24h)} tone="bg-emerald-500/15 text-emerald-500" />
        <KPI icon={EnvelopeIcon} label="Непрочитанных" value={fmt(statistics?.unread_messages)} tone="bg-amber-500/15 text-amber-500" />
        <KPI icon={EyeSlashIcon} label="Скрытых чатов" value={fmt(statistics ? statistics.deleted_chats ?? 0 : null)} tone="bg-rose-500/15 text-rose-500" />
      </div>

      <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-border-primary p-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Имя, телефон, email или #ID чата"
              aria-label="Поиск чатов"
              value={filters.search}
              onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
              className="h-9 w-full rounded-lg border border-border-primary bg-bg-primary pl-9 pr-8 text-sm text-text-primary placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30"
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => onFilterChange({ ...filters, search: '' })}
                aria-label="Очистить поиск"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-text-muted hover:text-text-primary"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-border-primary bg-bg-primary p-0.5">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s.value || 'all'}
                  type="button"
                  onClick={() => onFilterChange({ ...filters, status: s.value })}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    filters.status === s.value ? 'bg-bg-card text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <select
              aria-label="Сортировка"
              value={sortValue}
              onChange={(e) => {
                const [sort_by, sort_order] = e.target.value.split(':');
                onFilterChange({ ...filters, sort_by, sort_order });
              }}
              className="h-8 rounded-lg border border-border-primary bg-bg-primary px-2 text-xs font-medium text-text-primary focus:border-[#4F45E5] focus:outline-none"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onRefresh}
              title="Обновить"
              aria-label="Обновить"
              className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* List */}
        {error ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
            <p className="text-sm text-text-secondary">Не удалось загрузить чаты</p>
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Повторить
            </button>
          </div>
        ) : loading && chats.length === 0 ? (
          <ul className="divide-y divide-border-primary">
            {Array.from({ length: 8 }).map((_, i) => (
              <li key={i} className="flex animate-pulse items-center gap-4 px-4 py-3.5">
                <div className="flex -space-x-2">
                  <div className="h-8 w-8 rounded-full bg-bg-tertiary" />
                  <div className="h-8 w-8 rounded-full bg-bg-tertiary" />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
                  <div className="h-3 w-2/3 rounded bg-bg-tertiary" />
                </div>
                <div className="h-3 w-12 rounded bg-bg-tertiary" />
              </li>
            ))}
          </ul>
        ) : chats.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <ChatBubbleOvalLeftEllipsisIcon className="mx-auto mb-3 h-12 w-12 text-text-muted" />
            <p className="text-sm font-medium text-text-primary">Чаты не найдены</p>
            {(filters.search || filters.status) && (
              <button
                type="button"
                onClick={() => onFilterChange({ ...filters, search: '', status: '' })}
                className="mt-3 text-sm font-medium text-[#7C3AED] hover:underline"
              >
                Сбросить фильтры
              </button>
            )}
          </div>
        ) : (
          <ul className={`divide-y divide-border-primary transition-opacity ${loading ? 'opacity-60' : ''}`}>
            {chats.map((chat) => {
              const hidden = chat.status === 'deleted';
              const last = chat.last_message;
              const PreviewIcon = previewIcon(last?.message_type);
              const unreadFromLast = last && !last.is_read;
              return (
                <li key={chat.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => onChatSelect(chat)}
                    className={`flex w-full items-center gap-4 px-4 py-3 pr-14 text-left transition-colors hover:bg-bg-tertiary/60 ${hidden ? 'opacity-60' : ''}`}
                  >
                    <div className="flex shrink-0 -space-x-2">
                      <Avatar user={chat.user1} />
                      <Avatar user={chat.user2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-text-primary">
                          {chat.user1?.name || 'Пользователь'}
                          <span className="mx-1.5 font-normal text-text-muted">↔</span>
                          {chat.user2?.name || 'Пользователь'}
                        </span>
                        <span className="shrink-0 font-mono text-[11px] text-text-muted">#{chat.id}</span>
                        {hidden && (
                          <span className="shrink-0 rounded border border-rose-500/30 bg-rose-500/10 px-1.5 py-px text-[10px] font-semibold text-rose-500">
                            скрыт
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-text-secondary">
                        {unreadFromLast && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#4F45E5]" title="Не прочитано" />}
                        {PreviewIcon && <PreviewIcon className="h-3.5 w-3.5 shrink-0" />}
                        <span className="truncate">
                          {last ? (
                            last.message_type === 'image' && (!last.content || /^https?:\/\//.test(last.content))
                              ? 'Фото'
                              : last.content || (last.message_type === 'location' ? 'Геолокация' : 'Вложение')
                          ) : (
                            <i className="text-text-muted">Нет сообщений</i>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
                      <span className="text-xs text-text-muted" title={chat.updated_at ? new Date(chat.updated_at).toLocaleString('ru-RU') : ''}>
                        {relativeTime(chat.updated_at)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs tabular-nums text-text-secondary">
                        <ChatBubbleOvalLeftEllipsisIcon className="h-3.5 w-3.5" />
                        {fmt(chat.message_count || 0)}
                      </span>
                    </div>
                  </button>
                  {!hidden && (
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(null);
                        setConfirmChat(chat);
                      }}
                      title="Скрыть чат"
                      aria-label={`Скрыть чат #${chat.id}`}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-text-muted opacity-0 transition-opacity hover:bg-rose-500/10 hover:text-rose-500 focus:opacity-100 group-hover:opacity-100"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Pagination */}
        {!error && pagination.total > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-border-primary px-4 py-3">
            <span className="text-xs text-text-secondary">
              {fmt(from)}–{fmt(to)} из {fmt(pagination.total)}
            </span>
            {pagination.pages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                  aria-label="Предыдущая страница"
                  className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="min-w-[4.5rem] text-center text-sm font-medium text-text-primary">
                  {pagination.page} / {pagination.pages}
                </span>
                <button
                  type="button"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages || loading}
                  aria-label="Следующая страница"
                  className="rounded-lg border border-border-primary p-1.5 text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {confirmChat && (
        <ConfirmDialog
          title={`Скрыть чат #${confirmChat.id}?`}
          text={`Переписка ${confirmChat.user1?.name || ''} и ${confirmChat.user2?.name || ''} со всеми сообщениями будет скрыта у обоих пользователей. В админке чат останется в разделе «Скрытые».`}
          confirmLabel="Скрыть чат"
          busy={deleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => setConfirmChat(null)}
        />
      )}
    </div>
  );
};

export const ConfirmDialog = ({ title, text, confirmLabel, busy, error, onConfirm, onCancel }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}
    onKeyDown={(e) => e.key === 'Escape' && !busy && onCancel()}
    role="presentation"
  >
    <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-block border border-border-primary bg-bg-card p-5 text-left shadow-lg">
      <div className="flex gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-500">
          <TrashIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-text-primary">{title}</h3>
          <p className="mt-1 text-sm text-text-secondary">{text}</p>
          {error && <p className="mt-2 text-sm text-rose-500">{error}</p>}
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          autoFocus
          onClick={onCancel}
          disabled={busy}
          className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
        >
          {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

export default ChatList;
